import { useParams } from "@tanstack/react-router";
import { useSyncExternalStore } from "react";
import { type MeResponse, membresiaEnObra, useMe } from "@/lib/auth";
import { leerObraRecordada, suscribirObraActiva } from "@/lib/obra-activa";

/**
 * DestinoObra es el destino de navegación al cambiar de obra: la ruta (patrón de
 * TanStack, con `$obraId` si cuelga de una obra) y sus params.
 */
export type DestinoObra = { to: string; params?: Record<string, string> };

/**
 * resolverObraRecordada valida una obra recordada contra el `me`: solo sirve si el usuario
 * es **miembro** de ella. Es pura y síncrona, así que funciona igual sin red con el `me`
 * rehidratado de la caché offline.
 *
 * D8: sin atajo para el admin global. `/me` ya le lista todas las obras vivas, así que el
 * atajo `es_admin` no le habilitaba ninguna obra de más — solo dejaba pasar ids de obras
 * borradas o que ya no ve. Una obra de la que el usuario deja de ser miembro no puede
 * colarse en el menú porque esta validación se repite en cada resolución.
 */
export function resolverObraRecordada(
  me: MeResponse | undefined,
  id: number | undefined,
): number | undefined {
  if (me === undefined || id === undefined || !Number.isInteger(id) || id <= 0) {
    return undefined;
  }
  return membresiaEnObra(me, id) !== undefined ? id : undefined;
}

/**
 * destinoAlCambiarObra traduce la ruta actual a su equivalente en la obra elegida (D5):
 * el panel de la obra lleva al panel, una sección lleva a la misma sección y el detalle de
 * un registro lleva al listado de su sección (el id no existe en la otra obra). Desde una
 * ruta global (usuarios, gestión, sincronización) se permanece en ella: solo cambia la obra
 * activa. La excepción es el selector `/obras` (D10): ahí elegir una obra es entrar en ella,
 * igual que pulsar su tarjeta. Si la sección destino tiene guard propio, ese guard manda.
 */
export function destinoAlCambiarObra(pathname: string, nuevaObraId: number): DestinoObra {
  const segmentos = pathname.split("/").filter((s) => s !== "");
  if (segmentos[0] !== "obras") {
    return { to: pathname };
  }
  const params = { obraId: String(nuevaObraId) };
  const seccion = segmentos[2];
  return seccion === undefined
    ? { to: "/obras/$obraId", params }
    : { to: `/obras/$obraId/${seccion}`, params };
}

/**
 * useObraActiva es la fuente única de la obra activa (D6): la de la ruta si esta cuelga de
 * `/obras/$obraId`, y si no la recordada mientras siga siendo válida. Gracias a ella la
 * barra lateral y el conmutador no se vacían en las rutas globales. `useParams` (que se
 * apoya en el estado del router) la re-evalúa en cada navegación, `useMe` al cambiar el
 * usuario y la suscripción al recordar u olvidar la obra sin movernos de ruta.
 */
export function useObraActiva(): number | undefined {
  const { obraId } = useParams({ strict: false });
  const { data: me } = useMe();
  const recordada = useSyncExternalStore(suscribirObraActiva, leerObraRecordada);

  const deRuta = Number(obraId);
  if (Number.isInteger(deRuta) && deRuta > 0) {
    return deRuta;
  }
  return resolverObraRecordada(me, recordada);
}
